export enum GeometryShapeType {
    Rectangle = "rectangle",
    Triangle = "triangle",
    Ellipse = "ellipse",
    HouseShape = "house_shape",
    RightTrapezoid = "right_trapezoid",
    Complex = "complex"
}

export function translate(type: GeometryShapeType): string {
    switch (type) {
        case GeometryShapeType.Rectangle:
            return "Rektangel";
        case GeometryShapeType.Triangle:
            return "Triangel";
        case GeometryShapeType.Ellipse:
            return "Ellips";
        case GeometryShapeType.HouseShape:
            return "Husgavel";
        case GeometryShapeType.RightTrapezoid:
            return "Parallelltrapets";
        case GeometryShapeType.Complex:
            return "Komplex form";
    }
}

export interface GeometryShape {
    key: string;
    geometryType: string;
}

export interface RectangleShape extends GeometryShape {
    geometryType: "rectangle";
    width: number;
    height: number;
}

export interface TriangleShape extends GeometryShape {
    geometryType: "triangle";
    base: number;
    height: number;
}

export interface EllipseShape extends GeometryShape {
    geometryType: "ellipse";
    majorAxis: number;
    minorAxis: number;
}

export interface HouseShape extends GeometryShape {
    geometryType: "house_shape";
    width: number;
    rectangleHeight: number;
    triangleHeight: number;
}

export interface ComplexShape extends GeometryShape {
    geometryType: "complex";
    area: number;
}

export interface RightTrapezoidShape extends GeometryShape {
    geometryType: "right_trapezoid";
    baseWidth: number;
    leftHeight: number;
    rightHeight: number;
}

export function isRectangle(shape: GeometryShape): shape is RectangleShape {
    return shape.geometryType === "rectangle";
}

export function isTriangle(shape: GeometryShape): shape is TriangleShape {
    return shape.geometryType === "triangle";
}

export function isEllipse(shape: GeometryShape): shape is EllipseShape {
    return shape.geometryType === "ellipse";
}

export function isHouseShape(shape: GeometryShape): shape is HouseShape {
    return shape.geometryType === "house_shape";
}

export function isComplexShape(shape: GeometryShape): shape is ComplexShape {
    return shape.geometryType === "complex";
}

export function isRightTrapezoid(shape: GeometryShape): shape is RightTrapezoidShape {
    return shape.geometryType === "right_trapezoid";
}

export function computeShapeArea(shape: GeometryShape): number {
    if (isRectangle(shape)) {
        return shape.width * shape.height;
    } else if (isTriangle(shape)) {
        return 0.5 * shape.base * shape.height;
    } else if (isEllipse(shape)) {
        return Math.PI * (shape.majorAxis / 2) * (shape.minorAxis / 2);
    } else if (isHouseShape(shape)) {
        const rectangleArea = shape.width * shape.rectangleHeight;
        const triangleArea = 0.5 * shape.width * shape.triangleHeight;
        return rectangleArea + triangleArea;
    } else if (isRightTrapezoid(shape)) {
        const smallerHeight = Math.min(shape.leftHeight, shape.rightHeight);
        const rectangleArea = smallerHeight * shape.baseWidth;
        const heightDifference = Math.abs(shape.leftHeight - shape.rightHeight);
        const triangleArea = (heightDifference * shape.baseWidth) / 2;
        return rectangleArea + triangleArea;
    } else if (isComplexShape(shape)) {
        return shape.area;
    }

    return 0;
}

export function calculateRoofAngle(shape: RightTrapezoidShape): number | null {
    if (shape.leftHeight === shape.rightHeight) {
        return 0.0;
    }

    if (shape.baseWidth <= 0 || shape.leftHeight <= 0 || shape.rightHeight <= 0) {
        return null;
    }

    const heightDifference = Math.abs(shape.leftHeight - shape.rightHeight);
    const angle = Math.atan(heightDifference / shape.baseWidth);

    return angle;
}

export function calculateRoofAngleInDegrees(shape: RightTrapezoidShape): number | null {
    const angleInRadians = calculateRoofAngle(shape);
    if (angleInRadians === null) {
        return null;
    }
    return angleInRadians * (180 / Math.PI);
}

// Type alias for union of all shape types
export type AnyGeometryShape =
    | RectangleShape
    | TriangleShape
    | EllipseShape
    | HouseShape
    | ComplexShape
    | RightTrapezoidShape;